import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { property } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { distinctUntilChanged, from, fromEvent, map, of, startWith, switchMap, takeUntil, tap } from 'rxjs'
import { dragOffset } from './drag-offset'

/**
 * A host the user can drag from a grip and that remembers where it was left.
 * Nothing more: no panel, no trigger, no material of its own. The content
 * holds its own controls, the consumer paints the host and decides where it
 * rests, and the grip is whatever is slotted as `drag-handle` — its presence
 * is what switches the drag on, so a host that omits it is static.
 */
@schmancyElement('schmancy-dock')
export class SchmancyDock extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: inline-flex;
				align-items: center;
				flex-shrink: 0;
			}
			/* touch-action must sit on the grip's own box (a slot has none), and
			   only there: the content beside it keeps scrolling the page. It is
			   important rather than normal because the grip is the consumer's own
			   element and every normal declaration from the tree it lives in
			   outranks a ::slotted() one at any cascade layer — importance is the
			   one axis where the inner tree wins. Without it a consumer rule that
			   merely names touch-action ends the drag while cursor: grab still
			   paints the grip as live. The three cosmetic ones stay negotiable. */
			::slotted([slot='drag-handle']) {
				touch-action: none !important;
				cursor: grab;
				user-select: none;
				-webkit-user-select: none;
			}
			:host([dragging]) ::slotted([slot='drag-handle']) {
				cursor: grabbing;
			}
		`,
	]

	/** Persistence key — the sessionStorage entry is `schmancy-dock-{id}`. */
	@property({ type: String }) id: string = 'default'

	/** True while a drag session is past the tap threshold. Reflected for styling; read-only. */
	@property({ type: Boolean, reflect: true }) dragging = false

	private readonly handleSlot = createRef<HTMLSlotElement>()

	connectedCallback() {
		super.connectedCallback()
		// `draggable` would collide with the HTML5 attribute; the drag here is
		// pointer-driven, so a native ghost-image drag must never start.
		this.addEventListener('dragstart', e => e.preventDefault())
		from(this.updateComplete)
			.pipe(
				switchMap(() => {
					const slot = this.handleSlot.value!
					return fromEvent(slot, 'slotchange').pipe(
						startWith(null),
						map(() => slot.assignedElements().length > 0),
						distinctUntilChanged(),
						switchMap(hasHandle =>
							hasHandle
								? dragOffset(this, slot, `schmancy-dock-${this.id}`).pipe(
										tap(phase => {
											this.dragging = phase.dragging
										}),
									)
								: of(null).pipe(
										tap(() => {
											this.style.transform = ''
											this.dragging = false
										}),
									),
						),
					)
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	protected render(): unknown {
		return html`<slot name="drag-handle" ${ref(this.handleSlot)}></slot><slot></slot>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-dock': SchmancyDock
	}
}
